/**
 * PublishedCampaignStatus
 *
 * Interactive card shown in chat after campaign publishing completes.
 * Displays per-platform-account status with Activate / Pause toggle buttons.
 *
 * - Status is cached in DB (persists across reloads)
 * - Background sync silently refreshes from live platform on mount
 * - Each account row is independent (activate Meta, keep Google paused)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '@/services/api';
import type { PlatformAssignment } from '@/types/limits';

// ── Platform icons ────────────────────────────────────────────────────────────

const MetaIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current text-[#1877F2]" aria-label="Meta">
    <path d="M12 2.04C6.48 2.04 2 6.52 2 12.04c0 5.01 3.66 9.15 8.44 9.9v-7H7.9v-2.9h2.54V9.85c0-2.51 1.49-3.89 3.78-3.89 1.09 0 2.24.19 2.24.19v2.47h-1.26c-1.24 0-1.63.77-1.63 1.56v1.87h2.77l-.44 2.9h-2.33v7a10 10 0 0 0 8.44-9.9c0-5.52-4.48-10-10-10z" />
  </svg>
);

const GoogleAdsIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" aria-label="Google Ads">
    <path d="M15.5 2.1L9.7 12l2.6 4.5 5.8-10.1-2.6-4.3z" fill="#FBBC04" />
    <path d="M2.1 16.5h11.6l2.6-4.5H4.7L2.1 16.5z" fill="#4285F4" />
    <path d="M15.5 21.9c1.4 0 2.6-1.2 2.6-2.6s-1.2-2.6-2.6-2.6-2.6 1.2-2.6 2.6 1.2 2.6 2.6 2.6z" fill="#34A853" />
  </svg>
);

const TikTokIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current text-ink" aria-label="TikTok">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.75a4.85 4.85 0 0 1-1.01-.06z" />
  </svg>
);

const PinterestIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current text-[#E60023]" aria-label="Pinterest">
    <path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z" />
  </svg>
);

const TwitterIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current text-ink" aria-label="X (Twitter)">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.748l7.73-8.835L1.254 2.25H8.08l4.253 5.622L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const PlatformIcon: React.FC<{ platform: string }> = ({ platform }) => {
  const p = platform.toLowerCase();
  if (p === 'meta') return <MetaIcon />;
  if (p === 'google_ads' || p === 'google') return <GoogleAdsIcon />;
  if (p === 'tiktok') return <TikTokIcon />;
  if (p === 'pinterest') return <PinterestIcon />;
  if (p === 'twitter' || p === 'x') return <TwitterIcon />;
  return (
    <div className="w-5 h-5 rounded-full bg-inset flex items-center justify-center text-xs font-bold text-ink-3">
      {platform[0]?.toUpperCase()}
    </div>
  );
};

const getPlatformLabel = (platform: string) => {
  const map: Record<string, string> = {
    meta: 'Meta',
    google_ads: 'Google Ads',
    tiktok: 'TikTok',
    pinterest: 'Pinterest',
    twitter: 'X (Twitter)',
  };
  return map[platform.toLowerCase()] ?? platform;
};

// ── Status badge ──────────────────────────────────────────────────────────────

const StatusBadge: React.FC<{ status: PlatformAssignment['platform_campaign_status']; syncing?: boolean }> = ({ status, syncing }) => {
  if (syncing) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-inset text-ink-3">
        <svg className="w-3 h-3 animate-spin" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
        </svg>
        Syncing…
      </span>
    );
  }

  if (status === 'ACTIVE') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-ok-soft text-ok">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
        Active
      </span>
    );
  }
  if (status === 'PAUSED') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-warn-soft text-warn">
        <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
        Paused
      </span>
    );
  }
  if (status === 'ARCHIVED') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-inset text-ink-3">
        <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
        Archived
      </span>
    );
  }
  // null or UNKNOWN
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-inset text-ink-3">
      <span className="w-1.5 h-1.5 rounded-full bg-gray-300" />
      Unknown
    </span>
  );
};

// ── Toggle button ─────────────────────────────────────────────────────────────

const ToggleButton: React.FC<{
  assignment: PlatformAssignment;
  loading: boolean;
  onToggle: () => void;
}> = ({ assignment, loading, onToggle }) => {
  const canToggle = assignment.platform_campaign_id &&
    assignment.status === 'published' &&
    assignment.platform_campaign_status !== 'ARCHIVED';

  if (loading) {
    return (
      <button disabled className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-medium bg-inset text-ink-3 cursor-not-allowed">
        <svg className="w-3 h-3 animate-spin" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
        </svg>
        Updating…
      </button>
    );
  }

  if (!canToggle) {
    return (
      <button disabled className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-medium bg-inset text-gray-300 cursor-not-allowed">
        {assignment.status === 'failed' ? 'Failed' : 'Unavailable'}
      </button>
    );
  }

  if (assignment.platform_campaign_status === 'ACTIVE') {
    return (
      <button
        onClick={onToggle}
        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-medium border border-line-2 text-ink-2 hover:bg-ground transition-colors"
      >
        <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
          <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
        </svg>
        Pause
      </button>
    );
  }

  // PAUSED or UNKNOWN — show Activate
  return (
    <button
      onClick={onToggle}
      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-medium bg-primary text-white hover:bg-primary/90 transition-colors"
    >
      <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
        <path d="M8 5v14l11-7z" />
      </svg>
      Activate
    </button>
  );
};

// ── Relative time helper ──────────────────────────────────────────────────────

function relativeTime(isoString: string | null): string {
  if (!isoString) return 'never';
  const diffMs = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// ── Main component ────────────────────────────────────────────────────────────

interface Props {
  conversationId: string;
  initialAssignments: PlatformAssignment[];
}

function deduplicateAssignments(list: PlatformAssignment[]): PlatformAssignment[] {
  if (!Array.isArray(list)) return [];
  const seen = new Map<string, PlatformAssignment>();
  for (const a of list) {
    const key = a.platform_campaign_id ?? a.id;
    const existing = seen.get(key);
    if (!existing || (a.published_at ?? '') > (existing.published_at ?? '')) {
      seen.set(key, a);
    }
  }
  return Array.from(seen.values());
}

export const PublishedCampaignStatus: React.FC<Props> = ({ conversationId, initialAssignments }) => {
  const [assignments, setAssignments] = useState<PlatformAssignment[]>(() =>
    deduplicateAssignments(initialAssignments)
  );
  const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set());
  const [errorIds, setErrorIds] = useState<Record<string, string>>({});
  const [syncingIds, setSyncingIds] = useState<Set<string>>(new Set());
  const [lastSyncTime, setLastSyncTime] = useState<string | null>(null);

  // Background sync: fetch live status from platform for each published assignment
  const backgroundSync = useCallback(async () => {
    const toSync = assignments.filter(
      (a) => a.status === 'published' && a.platform_campaign_id
    );
    if (toSync.length === 0) return;

    setSyncingIds(new Set(toSync.map((a) => a.id)));

    await Promise.allSettled(
      toSync.map(async (a) => {
        try {
          const resp = await apiClient.get(
            `/api/v1/publisher/status/${a.platform}/${a.platform_campaign_id}`,
            { params: { account_id: a.platform_account_id } }
          );
          const liveStatus: string = (resp.data?.status || resp.data?.campaign_status || '').toUpperCase();
          if (!liveStatus) return;

          const normalized = ['ACTIVE', 'PAUSED', 'ARCHIVED'].includes(liveStatus)
            ? liveStatus as PlatformAssignment['platform_campaign_status']
            : 'UNKNOWN';

          if (normalized !== a.platform_campaign_status) {
            // Persist to DB
            await apiClient.patch(
              `/api/v1/conversations/${conversationId}/platform-assignments/${a.id}`,
              { platform_campaign_status: normalized }
            );
            // Update local state
            setAssignments((prev) =>
              prev.map((x) =>
                x.id === a.id
                  ? { ...x, platform_campaign_status: normalized, platform_campaign_status_synced_at: new Date().toISOString() }
                  : x
              )
            );
          }
        } catch {
          // Best-effort; ignore sync errors
        }
      })
    );

    setSyncingIds(new Set());
    setLastSyncTime(new Date().toISOString());
  }, [assignments, conversationId]);

  useEffect(() => {
    // Short delay so the card renders before sync kicks off
    const t = setTimeout(backgroundSync, 1500);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only on mount — intentionally not re-running on assignments change

  const toggleStatus = async (assignment: PlatformAssignment) => {
    if (!assignment.platform_campaign_id) return;
    const targetStatus = assignment.platform_campaign_status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE';

    setLoadingIds((prev) => new Set(prev).add(assignment.id));
    setErrorIds((prev) => { const n = { ...prev }; delete n[assignment.id]; return n; });

    try {
      // 1. Update on platform
      await apiClient.patch(
        `/api/v1/publisher/${assignment.platform}/campaigns/${assignment.platform_campaign_id}/status`,
        null,
        { params: { status: targetStatus, account_id: assignment.platform_account_id } }
      );

      // 2. Persist to DB
      await apiClient.patch(
        `/api/v1/conversations/${conversationId}/platform-assignments/${assignment.id}`,
        { platform_campaign_status: targetStatus }
      );

      // 3. Update local state
      setAssignments((prev) =>
        prev.map((a) =>
          a.id === assignment.id
            ? { ...a, platform_campaign_status: targetStatus as PlatformAssignment['platform_campaign_status'], platform_campaign_status_synced_at: new Date().toISOString() }
            : a
        )
      );
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Update failed';
      setErrorIds((prev) => ({ ...prev, [assignment.id]: msg }));
    } finally {
      setLoadingIds((prev) => {
        const n = new Set(prev);
        n.delete(assignment.id);
        return n;
      });
    }
  };

  const publishedCount = assignments.filter((a) => a.status === 'published').length;
  const failedCount = assignments.filter((a) => a.status === 'failed').length;
  const scheduledCount = assignments.filter((a) => a.status === 'scheduled').length;
  const allScheduled = scheduledCount > 0 && scheduledCount === assignments.length;
  const hasOrganic = assignments.some((a) => a.publish_type === 'organic_post');
  const allSyncTimes = assignments
    .map((a) => a.platform_campaign_status_synced_at)
    .filter(Boolean) as string[];
  const sortedSyncTimes = allSyncTimes.sort();
  const mostRecentSync = lastSyncTime ?? (sortedSyncTimes.length > 0 ? sortedSyncTimes[sortedSyncTimes.length - 1] : null);

  return (
    <div className="w-full rounded border border-emerald-200 bg-ok-soft/30 overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-emerald-200 bg-card/70">
        <span className="flex items-center justify-center w-5 h-5 rounded-full bg-ok-soft flex-shrink-0">
          <svg className="w-3 h-3 text-ok" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </span>
        <span className="text-sm font-semibold text-ink">
          {allScheduled ? 'Campaign scheduled' : hasOrganic ? 'Content published' : 'Campaign publishing complete'}
        </span>
        <span className="ml-auto text-xs text-ink-3">
          {allScheduled
            ? `${scheduledCount} scheduled`
            : `${publishedCount} published${failedCount > 0 ? `, ${failedCount} failed` : ''}`}
        </span>
      </div>

      {/* Table */}
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-emerald-200/60 bg-card/40">
            <th className="px-4 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-ink-3">Account</th>
            <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-ink-3">Campaign ID</th>
            <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-ink-3">Status</th>
            <th className="px-4 py-2 text-right text-[10px] font-semibold uppercase tracking-wider text-ink-3">Action</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-emerald-100">
          {assignments.map((a) => (
            <tr
              key={a.id}
              className={`bg-card/50 transition-opacity ${loadingIds.has(a.id) ? 'opacity-60' : ''}`}
            >
              {/* Account */}
              <td className="px-4 py-2.5">
                <div className="flex items-center gap-2">
                  <PlatformIcon platform={a.platform} />
                  <div className="min-w-0">
                    <span className="block text-xs font-medium text-ink truncate">
                      {a.account_name || 'Unnamed Account'}
                    </span>
                    <span className="text-[10px] text-ink-3">
                      {getPlatformLabel(a.platform)}
                    </span>
                  </div>
                </div>
              </td>

              {/* Campaign ID */}
              <td className="px-3 py-2.5 max-w-[140px]">
                {a.platform_campaign_id ? (
                  <span className="font-mono text-[10px] text-ink-3 truncate block">
                    {a.platform_campaign_id}
                  </span>
                ) : (
                  <span className="text-[10px] text-gray-300">—</span>
                )}
              </td>

              {/* Status */}
              <td className="px-3 py-2.5">
                {a.status === 'scheduled' ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-work-soft text-work">
                    <span className="w-1.5 h-1.5 rounded-full bg-navy" />
                    Scheduled
                  </span>
                ) : a.publish_type === 'organic_post' ? (
                  a.status === 'failed' ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-bad-soft text-bad">
                      <span className="w-1.5 h-1.5 rounded-full bg-bad" />
                      Post failed
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-ok-soft text-ok">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                      Posted
                    </span>
                  )
                ) : (
                  <>
                    <StatusBadge
                      status={a.status === 'failed' ? null : a.platform_campaign_status}
                      syncing={syncingIds.has(a.id)}
                    />
                    {a.status === 'failed' && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-bad-soft text-bad mt-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-bad" />
                        Failed
                      </span>
                    )}
                  </>
                )}
              </td>

              {/* Action */}
              <td className="px-4 py-2.5 text-right">
                {a.status === 'scheduled' ? null : a.publish_type === 'organic_post' ? (
                  a.organic_post_urls && a.organic_post_urls.length > 0 ? (
                    <a
                      href={a.organic_post_urls[0]}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-primary hover:underline"
                    >
                      View post ↗
                    </a>
                  ) : null
                ) : (
                  <ToggleButton
                    assignment={a}
                    loading={loadingIds.has(a.id)}
                    onToggle={() => toggleStatus(a)}
                  />
                )}
                {errorIds[a.id] && (
                  <p className="text-[10px] text-bad mt-1">{errorIds[a.id]}</p>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Footer */}
      <div className="flex items-center justify-between gap-2 flex-wrap px-4 py-2 border-t border-emerald-200/60 bg-card/40">
        <p className="text-[10px] text-ink-3">
          {mostRecentSync ? `Synced ${relativeTime(mostRecentSync)}` : 'Status not yet synced'}
          <span className="mx-1.5 text-gray-300">·</span>
          Once active, ask <span className="italic">"How is my campaign performing?"</span>
        </p>
        <Link
          to="/results"
          className="text-xs text-primary hover:underline font-medium flex-shrink-0"
        >
          Analytics Dashboard →
        </Link>
      </div>
    </div>
  );
};
